import React, { useCallback, useEffect, useState } from "react";
import { Button, DatePicker, Select, Table } from "antd";
import dayjs from "dayjs";
import { Coins, RefreshCw, ShieldCheck } from "lucide-react";

import {
    fetchLlmBalance,
    fetchLlmUsageRequests,
    fetchLlmUsageSummary
} from "../api/adminMaintenanceApi";
import styles from "./LlmUsagePanel.module.css";

const { RangePicker } = DatePicker;
const numberFormatter = new Intl.NumberFormat("ru-RU");

const formatNumber = value => numberFormatter.format(Number(value) || 0);
const formatDateTime = value => value ? dayjs(value).format("DD.MM.YYYY HH:mm:ss") : "—";
const apiDateTime = value => value.format("YYYY-MM-DDTHH:mm:ss");

function normalizeRequest(request) {
    return {
        ...request,
        createdAt: request.createdAt ?? request.created_at,
        requestId: request.requestId ?? request.request_id,
        employeeId: request.employeeId ?? request.employee_id,
        employeeEmail: request.employeeEmail ?? request.employee_email,
        requestPath: request.requestPath ?? request.request_path,
        promptTokens: request.promptTokens ?? request.prompt_tokens,
        completionTokens: request.completionTokens ?? request.completion_tokens,
        totalTokens: request.totalTokens ?? request.total_tokens,
        errorType: request.errorType ?? request.error_type
    };
}

function getBalanceRows(balance) {
    const rows = balance?.balance_infos || balance?.balanceInfos || [];
    return Array.isArray(rows) ? rows : [];
}

export default function LlmUsagePanel() {
    const [period, setPeriod] = useState([dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")]);
    const [employeeId, setEmployeeId] = useState(null);
    const [summary, setSummary] = useState(null);
    const [requests, setRequests] = useState([]);
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [balanceLoading, setBalanceLoading] = useState(true);
    const [error, setError] = useState("");
    const [balanceError, setBalanceError] = useState("");

    const loadUsage = useCallback(async () => {
        if (!period?.[0] || !period?.[1]) return;

        setLoading(true);
        setError("");
        const params = { from: apiDateTime(period[0]), to: apiDateTime(period[1]) };

        try {
            const [nextSummary, nextRequests] = await Promise.all([
                fetchLlmUsageSummary(params),
                fetchLlmUsageRequests({ ...params, employeeId })
            ]);
            setSummary(nextSummary);
            setRequests(Array.isArray(nextRequests) ? nextRequests.map(normalizeRequest) : []);
        } catch (requestError) {
            setError(requestError.message || "Не удалось загрузить статистику LLM");
        } finally {
            setLoading(false);
        }
    }, [employeeId, period]);

    const loadBalance = useCallback(async () => {
        setBalanceLoading(true);
        setBalanceError("");
        try {
            setBalance(await fetchLlmBalance());
        } catch (requestError) {
            setBalanceError(requestError.message || "Не удалось получить баланс DeepSeek");
        } finally {
            setBalanceLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsage();
    }, [loadUsage]);

    useEffect(() => {
        loadBalance();
    }, [loadBalance]);

    const users = summary?.users || [];
    const total = summary?.total || {};
    const balanceRows = getBalanceRows(balance);
    const employeeOptions = users
        .filter(user => user.employee_id !== null && user.employee_id !== undefined)
        .map(user => ({
            value: String(user.employee_id),
            label: user.employee_email || `Сотрудник #${user.employee_id}`
        }));

    const summaryColumns = [
        {
            title: "Сотрудник",
            dataIndex: "employee_email",
            key: "employee_email",
            render: (email, row) => email || (row.employee_id ? `Сотрудник #${row.employee_id}` : "Не определён")
        },
        { title: "Запросы", dataIndex: "requests", key: "requests", align: "right", render: formatNumber },
        { title: "Успешно", dataIndex: "successful_requests", key: "successful_requests", align: "right", render: formatNumber },
        { title: "Ошибки", dataIndex: "failed_requests", key: "failed_requests", align: "right", render: formatNumber },
        { title: "Входные токены", dataIndex: "prompt_tokens", key: "prompt_tokens", align: "right", render: formatNumber },
        { title: "Выходные токены", dataIndex: "completion_tokens", key: "completion_tokens", align: "right", render: formatNumber },
        { title: "Всего токенов", dataIndex: "total_tokens", key: "total_tokens", align: "right", render: value => <strong>{formatNumber(value)}</strong> }
    ];

    const requestColumns = [
        {
            title: "Время",
            key: "createdAt",
            width: 160,
            render: (_, row) => formatDateTime(row.created_at ?? row.createdAt)
        },
        {
            title: "Сотрудник",
            key: "employeeEmail",
            render: (_, row) => row.employee_email ?? row.employeeEmail ?? "Не определён"
        },
        { title: "Операция", dataIndex: "action", key: "action", render: value => value || "—" },
        { title: "Модель", dataIndex: "model", key: "model", render: value => value || "—" },
        {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            render: value => <span className={value === "success" ? styles.statusSuccess : styles.statusError}>{value === "success" ? "Успешно" : "Ошибка"}</span>
        },
        {
            title: "Вход",
            key: "promptTokens",
            align: "right",
            render: (_, row) => formatNumber(row.prompt_tokens ?? row.promptTokens)
        },
        {
            title: "Выход",
            key: "completionTokens",
            align: "right",
            render: (_, row) => formatNumber(row.completion_tokens ?? row.completionTokens)
        },
        {
            title: "Всего",
            key: "totalTokens",
            align: "right",
            render: (_, row) => formatNumber(row.total_tokens ?? row.totalTokens)
        },
        {
            title: "Тип ошибки",
            key: "errorType",
            render: (_, row) => row.error_type ?? row.errorType ?? "—"
        }
    ];

    return (
        <section className={styles.panel}>
            <div className={styles.header}>
                <div className={styles.heading}>
                    <span className={styles.icon}><Coins size={22} /></span>
                    <div>
                        <h2>Использование LLM</h2>
                        <p>Учёт обращений и токенов без сохранения промптов, ответов и документов.</p>
                    </div>
                </div>
                <div className={styles.controls}>
                    <RangePicker
                        value={period}
                        onChange={dates => dates && setPeriod([dates[0].startOf("day"), dates[1].endOf("day")])}
                        allowClear={false}
                        format="DD.MM.YYYY"
                    />
                    <Select
                        value={employeeId}
                        onChange={setEmployeeId}
                        options={employeeOptions}
                        placeholder="Все сотрудники"
                        allowClear
                        className={styles.employeeSelect}
                    />
                    <Button icon={<RefreshCw size={16} />} loading={loading} onClick={loadUsage}>Обновить</Button>
                </div>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.kpis}>
                <div><span>Запросов</span><strong>{formatNumber(total.requests)}</strong></div>
                <div><span>Ошибок</span><strong>{formatNumber(total.failed_requests)}</strong></div>
                <div><span>Входных токенов</span><strong>{formatNumber(total.prompt_tokens)}</strong></div>
                <div><span>Выходных токенов</span><strong>{formatNumber(total.completion_tokens)}</strong></div>
                <div><span>Всего токенов</span><strong>{formatNumber(total.total_tokens)}</strong></div>
            </div>

            <div className={styles.balanceBlock}>
                <div className={styles.sectionTitle}>
                    <div><ShieldCheck size={18} /><h3>Баланс DeepSeek</h3></div>
                    <Button size="small" loading={balanceLoading} onClick={loadBalance}>Обновить баланс</Button>
                </div>
                {balanceError ? <div className={styles.balanceError}>{balanceError}</div> : (
                    <div className={styles.balanceRows}>
                        {balanceRows.map(item => (
                            <div className={styles.balanceItem} key={item.currency}>
                                <span>{item.currency}</span>
                                <strong>{item.total_balance ?? item.totalBalance ?? "—"}</strong>
                                <small>Пополнено: {item.topped_up_balance ?? item.toppedUpBalance ?? "—"} · Бонус: {item.granted_balance ?? item.grantedBalance ?? "—"}</small>
                            </div>
                        ))}
                        {!balanceLoading && balanceRows.length === 0 && <span className={styles.muted}>Баланс не предоставлен API.</span>}
                    </div>
                )}
            </div>

            <div className={styles.tableSection}>
                <h3>По сотрудникам</h3>
                <Table columns={summaryColumns} dataSource={users} rowKey={row => row.employee_id ?? `unknown-${row.employee_email}`} loading={loading} size="small" scroll={{ x: 1050 }} pagination={false} />
            </div>

            <div className={styles.tableSection}>
                <h3>Последние обращения <span>до 200 записей</span></h3>
                <Table columns={requestColumns} dataSource={requests} rowKey="id" loading={loading} size="small" scroll={{ x: 1100 }} pagination={{ pageSize: 25, showSizeChanger: false }} />
            </div>
        </section>
    );
}
